import { useEffect, useMemo, useState } from "react";
import { styles } from "../styles";
import { displayName, hasCapability } from "../utils/capabilities";
import { db } from "../firebase";
import { collection, onSnapshot, query, where } from "firebase/firestore";
import { DEFAULT_PAY_TYPES } from "./MyTimePage";
import BackButton from "../components/BackButton";
import {
  groupBy as groupByRows,
  dayKey,
  weekKey,
  monthKey,
  yearKey,
  labelForDateKey,
  currentPayPeriodBounds,
} from "../utils/aggregation";

const STATUS_BADGE = {
  pending: { label: "Pending", color: "#9a6b00", bg: "#fff3cd" },
  approved: { label: "Approved", color: "#1a7f37", bg: "#c9efc9" },
  locked: { label: "Locked", color: "#3d4a5c", bg: "#dde7f4" },
};

const formatDuration = (mins) => {
  if (!mins || mins <= 0) return "0:00";
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return `${h}:${String(m).padStart(2, "0")}`;
};

const formatDate = (iso) => (iso ? new Date(iso).toLocaleDateString() : "—");
const formatDateTime = (iso) => (iso ? new Date(iso).toLocaleString() : "—");

const downloadCsv = (rows, filename) => {
  const escape = (v) => {
    const s = String(v ?? "");
    if (/[",\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
    return s;
  };
  const csv = rows.map((r) => r.map(escape).join(",")).join("\n");
  const blob = new Blob([csv], { type: "text/csv" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
};

const slugId = (name) =>
  name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "") || `pt-${Date.now()}`;

export default function PayrollPage({
  orgId,
  role,
  capabilities,
  actions,
  customers = [],
  timeEntriesRaw = [],
  jobsRaw = [],
  payTypes,
  payPeriodConfig,
  onBack,
}) {
  const canApprove = hasCapability(role, capabilities, "approveTime");
  const effectivePayTypes = (payTypes && payTypes.length > 0)
    ? payTypes
    : DEFAULT_PAY_TYPES;

  const [orgUsers, setOrgUsers] = useState([]);
  useEffect(() => {
    if (!orgId) return;
    const q = query(collection(db, "users"), where("orgId", "==", orgId));
    const unsub = onSnapshot(q, (snap) =>
      setOrgUsers(snap.docs.map((d) => ({ id: d.id, ...d.data() })))
    );
    return unsub;
  }, [orgId]);

  const userById = useMemo(() => {
    const m = new Map();
    for (const u of orgUsers) m.set(u.id, u);
    return m;
  }, [orgUsers]);

  const [tab, setTab] = useState("pending");
  const [techFilter, setTechFilter] = useState("");
  const [customerFilter, setCustomerFilter] = useState("");
  const [locationFilter, setLocationFilter] = useState("");
  const [jobFilter, setJobFilter] = useState("");
  const [range, setRange] = useState("week");

  // Piece 1: Detail / Overview view toggle. Default Detail so existing
  // muscle memory works. Overview rolls `filtered` up into buckets.
  const [view, setView] = useState("detail");
  const [groupByMode, setGroupByMode] = useState("tech");
  const [overviewSort, setOverviewSort] = useState({
    col: "label",
    dir: "asc",
  });

  // Default sort flips when switching between name buckets (alphabetical)
  // and date buckets (chronological, newest first).
  useEffect(() => {
    if (["day", "week", "month", "year"].includes(groupByMode)) {
      setOverviewSort({ col: "key", dir: "desc" });
    } else {
      setOverviewSort({ col: "label", dir: "asc" });
    }
  }, [groupByMode]);

  // Live pay-period bounds (Piece 2). Recomputed each render; only
  // expensive once per day since the bounds change at midnight.
  // currentPayPeriodBounds returns null if org has no config set --
  // the "current_period" range option gracefully degrades to "all".
  const payPeriodBounds = useMemo(
    () => currentPayPeriodBounds(payPeriodConfig),
    [payPeriodConfig]
  );

  // Effective start day for the Group-by-Week bucket -- uses org
  // config if present, else Monday.
  const weekStartDay = payPeriodConfig?.startDayOfWeek || "monday";

  // Compute the range window. "current_period" pulls bounds from the
  // org config; the others are rolling N-day windows as before. "all"
  // and "current_period" without a config = no cutoff.
  const rangeWindow = useMemo(() => {
    if (range === "all") return { startMs: 0, endMs: Infinity };
    if (range === "current_period") {
      if (!payPeriodBounds) return { startMs: 0, endMs: Infinity };
      return {
        startMs: payPeriodBounds.startMs,
        endMs: payPeriodBounds.endMs,
      };
    }
    const days = range === "week" ? 7 : range === "month" ? 30 : 90;
    return {
      startMs: Date.now() - days * 86400000,
      endMs: Infinity,
    };
  }, [range, payPeriodBounds]);

  const filtered = useMemo(() => {
    let list = timeEntriesRaw.slice();
    if (tab === "pending")
      list = list.filter((e) => (e.status || "pending") === "pending");
    if (tab === "approved") list = list.filter((e) => e.status === "approved");
    if (tab === "locked") list = list.filter((e) => e.status === "locked");
    if (techFilter) list = list.filter((e) => e.userId === techFilter);
    if (jobFilter) {
      list = list.filter((e) => e.jobId === jobFilter);
    } else if (locationFilter) {
      const locJobs = new Set(
        jobsRaw.filter((j) => j.locationId === locationFilter).map((j) => j.id)
      );
      list = list.filter((e) => locJobs.has(e.jobId));
    } else if (customerFilter) {
      const custJobs = new Set(
        jobsRaw.filter((j) => j.customerId === customerFilter).map((j) => j.id)
      );
      list = list.filter((e) => custJobs.has(e.jobId));
    }
    if (rangeWindow.startMs > 0 || rangeWindow.endMs !== Infinity) {
      list = list.filter((e) => {
        const t = new Date(e.endAt || e.startAt || e.createdAt).getTime();
        return t >= rangeWindow.startMs && t < rangeWindow.endMs;
      });
    }
    return list.sort(
      (a, b) =>
        new Date(b.startAt || b.createdAt) -
        new Date(a.startAt || a.createdAt)
    );
  }, [
    timeEntriesRaw,
    tab,
    techFilter,
    customerFilter,
    locationFilter,
    jobFilter,
    rangeWindow,
    jobsRaw,
  ]);

  const payTypeMultiplier = (id) => {
    const pt = effectivePayTypes.find((p) => p.id === id);
    return pt ? Number(pt.multiplier) || 1 : 1;
  };
  const payTypeLabel = (id) => {
    const pt = effectivePayTypes.find((p) => p.id === id);
    return pt ? pt.name : id || "Straight Time";
  };

  const totalMinutes = filtered.reduce(
    (sum, e) => sum + (e.durationMinutes || 0),
    0
  );
  const totalDollars = filtered.reduce((sum, e) => {
    const u = userById.get(e.userId);
    const rate = parseFloat(u?.hourlyRate);
    if (!rate || !e.durationMinutes) return sum;
    const mult = payTypeMultiplier(e.payType);
    return sum + (e.durationMinutes / 60) * rate * mult;
  }, 0);

  const filterCustomer = customers.find((c) => c.id === customerFilter);
  const filterLocations = filterCustomer?.locations || [];
  const filterJobs = jobsRaw.filter(
    (j) =>
      (!customerFilter || j.customerId === customerFilter) &&
      (!locationFilter || j.locationId === locationFilter)
  );

  const jobLabel = (jobId) => {
    if (!jobId) return "(no job)";
    const j = jobsRaw.find((x) => x.id === jobId);
    return j
      ? `${j.jobNumber ? `#${j.jobNumber} ` : ""}${j.title || j.id.slice(0, 6)}`
      : `Job ${jobId.slice(0, 6)}`;
  };

  // Job -> customer lookup, built once per render. Used by the customer
  // overview bucket to roll time entries up through their job.
  const customerByJobId = useMemo(() => {
    const m = new Map();
    for (const j of jobsRaw) m.set(j.id, j.customerId);
    return m;
  }, [jobsRaw]);

  const customerNameById = (id) =>
    customers.find((c) => c.id === id)?.name || "—";

  // Overview rows: same `filtered` set as Detail, just rolled up. Tech /
  // job / customer buckets share the dollar math with the page totals;
  // date buckets (day/week/month/year) use the new utils/aggregation
  // date helpers. Sort applied after bucketing.
  const overviewRows = useMemo(() => {
    if (view !== "overview") return [];

    let keyFn;
    let makeBucket;
    if (groupByMode === "tech") {
      keyFn = (e) => e.userId || "(unknown)";
      makeBucket = (key, e) => {
        const u = userById.get(key);
        return {
          key,
          label: u
            ? displayName(u.profile, u.email)
            : e.userName || e.userEmail || "Unknown",
          minutes: 0,
          dollars: 0,
          entries: 0,
        };
      };
    } else if (groupByMode === "job") {
      keyFn = (e) => e.jobId || "(no job)";
      makeBucket = (key) => ({
        key,
        label: key === "(no job)" ? "(no job)" : jobLabel(key),
        minutes: 0,
        dollars: 0,
        entries: 0,
      });
    } else if (groupByMode === "customer") {
      keyFn = (e) => customerByJobId.get(e.jobId) || "(no customer)";
      makeBucket = (key) => ({
        key,
        label:
          key === "(no customer)" ? "(no customer)" : customerNameById(key),
        minutes: 0,
        dollars: 0,
        entries: 0,
      });
    } else if (groupByMode === "day") {
      keyFn = (e) => dayKey(e.startAt || e.createdAt);
      makeBucket = (key) => ({
        key,
        label: labelForDateKey("day", key),
        minutes: 0,
        dollars: 0,
        entries: 0,
      });
    } else if (groupByMode === "week") {
      // Honors org payPeriodConfig.startDayOfWeek; defaults to Monday.
      keyFn = (e) => weekKey(e.startAt || e.createdAt, weekStartDay);
      makeBucket = (key) => ({
        key,
        label: labelForDateKey("week", key),
        minutes: 0,
        dollars: 0,
        entries: 0,
      });
    } else if (groupByMode === "month") {
      keyFn = (e) => monthKey(e.startAt || e.createdAt);
      makeBucket = (key) => ({
        key,
        label: labelForDateKey("month", key),
        minutes: 0,
        dollars: 0,
        entries: 0,
      });
    } else if (groupByMode === "year") {
      keyFn = (e) => yearKey(e.startAt || e.createdAt);
      makeBucket = (key) => ({
        key,
        label: labelForDateKey("year", key),
        minutes: 0,
        dollars: 0,
        entries: 0,
      });
    } else {
      return [];
    }

    const reduce = (bucket, e) => {
      bucket.minutes += e.durationMinutes || 0;
      const u = userById.get(e.userId);
      const rate = parseFloat(u?.hourlyRate);
      if (rate && e.durationMinutes) {
        bucket.dollars +=
          (e.durationMinutes / 60) * rate * payTypeMultiplier(e.payType);
      }
      bucket.entries += 1;
    };

    const buckets = groupByRows(filtered, keyFn, makeBucket, reduce);
    const { col, dir } = overviewSort;
    const sign = dir === "asc" ? 1 : -1;
    buckets.sort((a, b) => {
      if (col === "label") return sign * a.label.localeCompare(b.label);
      if (col === "minutes") return sign * (a.minutes - b.minutes);
      if (col === "dollars") return sign * (a.dollars - b.dollars);
      if (col === "entries") return sign * (a.entries - b.entries);
      if (col === "key") return sign * String(a.key).localeCompare(String(b.key));
      return 0;
    });
    return buckets;
  }, [
    view,
    filtered,
    groupByMode,
    overviewSort,
    userById,
    customerByJobId,
    customers,
    jobsRaw,
  ]); // eslint-disable-line react-hooks/exhaustive-deps

  // Drill-down: click an overview row to jump to Detail filtered by that
  // bucket. Date buckets switch view but don't apply a date filter --
  // custom date ranges land with Piece 2 pay-period work.
  const handleOverviewRowClick = (bucket) => {
    if (groupByMode === "tech") {
      setTechFilter(bucket.key === "(unknown)" ? "" : bucket.key);
    } else if (groupByMode === "job") {
      setJobFilter(bucket.key === "(no job)" ? "" : bucket.key);
    } else if (groupByMode === "customer") {
      setCustomerFilter(
        bucket.key === "(no customer)" ? "" : bucket.key
      );
      setLocationFilter("");
      setJobFilter("");
    }
    setView("detail");
  };

  const toggleSort = (col) => {
    setOverviewSort((s) =>
      s.col === col
        ? { col, dir: s.dir === "asc" ? "desc" : "asc" }
        : { col, dir: col === "label" || col === "key" ? "asc" : "desc" }
    );
  };

  const handleExport = () => {
    const today = new Date().toISOString().slice(0, 10);
    // When the current-period range is active, embed the bounds in
    // the filename so the bookkeeper's downloads aren't all dated
    // "today" -- they read "payroll-2026-06-08-to-2026-06-21.csv".
    const periodTag =
      range === "current_period" && payPeriodBounds
        ? `${new Date(payPeriodBounds.startMs)
            .toISOString()
            .slice(0, 10)}-to-${new Date(payPeriodBounds.endMs - 86400000)
            .toISOString()
            .slice(0, 10)}`
        : today;

    // Overview mode: one row per bucket. Matches what's on screen.
    if (view === "overview") {
      const groupColLabel =
        groupByMode === "tech"
          ? "Tech"
          : groupByMode === "job"
          ? "Job"
          : groupByMode === "customer"
          ? "Customer"
          : groupByMode === "day"
          ? "Day"
          : groupByMode === "week"
          ? "Week"
          : groupByMode === "month"
          ? "Month"
          : "Year";
      const header = [
        groupColLabel,
        "Hours (h:mm)",
        "Hours (decimal)",
        "Labor Cost ($)",
        "Entries",
      ];
      const rows = overviewRows.map((b) => [
        b.label,
        formatDuration(b.minutes),
        (b.minutes / 60).toFixed(2),
        b.dollars > 0 ? b.dollars.toFixed(2) : "",
        b.entries,
      ]);
      downloadCsv(
        [header, ...rows],
        `payroll-overview-${groupByMode}-${tab}-${periodTag}.csv`
      );
      return;
    }

    // Detail mode: one row per time entry (unchanged).
    const header = [
      "Date",
      "Tech",
      "Job",
      "Source",
      "Pay Type",
      "Multiplier",
      "Start Date",
      "End Date",
      "Duration (h:mm)",
      "Hours (decimal)",
      "Rate ($/hr)",
      "Effective Rate ($/hr)",
      "Cost ($)",
      "Status",
      "Notes",
    ];
    const rows = filtered.map((e) => {
      const u = userById.get(e.userId);
      const rate = parseFloat(u?.hourlyRate) || 0;
      const mult = payTypeMultiplier(e.payType);
      const decHours = (e.durationMinutes || 0) / 60;
      const effectiveRate = rate * mult;
      const cost = effectiveRate * decHours;
      // Ticket-fed entries written before the Cloud Function fix have
      // startAt: null -- fall back to createdAt so the Date column
      // populates instead of showing "—" for historical data.
      const workIso = e.startAt || e.createdAt;
      const endIso = e.endAt || workIso;
      return [
        formatDate(workIso),
        u
          ? displayName(u.profile, u.email)
          : e.userName || e.userEmail || "",
        jobLabel(e.jobId),
        e.source,
        payTypeLabel(e.payType),
        mult,
        formatDate(workIso),
        formatDate(endIso),
        formatDuration(e.durationMinutes),
        decHours.toFixed(2),
        rate ? rate.toFixed(2) : "",
        rate ? effectiveRate.toFixed(2) : "",
        rate ? cost.toFixed(2) : "",
        e.status || "pending",
        e.notes || "",
      ];
    });
    downloadCsv([header, ...rows], `payroll-${tab}-${periodTag}.csv`);
  };

  const handleBulkApprove = async () => {
    if (!window.confirm(`Approve all ${filtered.length} entries shown?`)) return;
    for (const e of filtered) {
      if ((e.status || "pending") === "pending") {
        await actions.approveTimeEntry(e.id);
      }
    }
  };

  const handleSetEntryPayType = (entryId, newPayType) =>
    actions.updateTimeEntry(entryId, { payType: newPayType });

  // --- Configure Pay Types ---
  const [showConfig, setShowConfig] = useState(false);
  const [draft, setDraft] = useState(null);
  useEffect(() => {
    if (showConfig) setDraft(effectivePayTypes.map((p) => ({ ...p })));
  }, [showConfig]); // eslint-disable-line react-hooks/exhaustive-deps
  const [savingConfig, setSavingConfig] = useState(false);
  const [configErr, setConfigErr] = useState("");

  const handleSaveConfig = async () => {
    setConfigErr("");
    const seen = new Set();
    const cleaned = [];
    for (const p of draft || []) {
      const name = (p.name || "").trim();
      const mult = Number(p.multiplier);
      if (!name) {
        setConfigErr("Every pay type needs a name.");
        return;
      }
      if (!Number.isFinite(mult) || mult <= 0) {
        setConfigErr(`"${name}" needs a positive multiplier.`);
        return;
      }
      const id = (p.id || "").trim() || slugId(name);
      if (seen.has(id)) {
        setConfigErr(`Duplicate id: ${id}. Each pay type needs a unique id.`);
        return;
      }
      seen.add(id);
      cleaned.push({ id, name, multiplier: mult });
    }
    try {
      setSavingConfig(true);
      await actions.setOrgPayTypes(cleaned);
      setShowConfig(false);
    } catch (e) {
      setConfigErr(e.message);
    } finally {
      setSavingConfig(false);
    }
  };

  if (!canApprove) {
    return (
      <>
        <BackButton onClick={onBack} />
        <h2>Payroll</h2>
        <p style={{ color: "#666" }}>
          You don't have permission to manage payroll. Ask your admin to grant
          the "Approve / Lock Time Entries" capability.
        </p>
      </>
    );
  }

  return (
    <>
      <BackButton onClick={onBack} />
      <h2>Payroll</h2>

      <div style={{ ...styles.card, maxWidth: "780px" }}>
        <button
          style={styles.button}
          onClick={() => setShowConfig((s) => !s)}
        >
          {showConfig ? "Hide" : "Configure"} Pay Types
        </button>
        {showConfig && draft && (
          <div style={{ marginTop: "10px" }}>
            <p style={{ fontSize: "12px", color: "#666", margin: "0 0 8px" }}>
              Multiplier applies to the employee's hourly rate. Straight time
              should stay at 1.0; over time is typically 1.5; double 2.0. Add
              custom ones (Holiday, Travel, Premium, etc.) as needed.
            </p>
            {draft.map((p, idx) => (
              <div
                key={idx}
                style={{
                  display: "flex",
                  gap: "8px",
                  alignItems: "center",
                  marginBottom: "6px",
                  flexWrap: "wrap",
                }}
              >
                <input
                  style={{ ...styles.input, margin: 0, width: "200px" }}
                  type="text"
                  placeholder="Name (e.g. Holiday)"
                  value={p.name}
                  onChange={(e) => {
                    const next = draft.slice();
                    next[idx] = { ...next[idx], name: e.target.value };
                    setDraft(next);
                  }}
                />
                <input
                  style={{ ...styles.input, margin: 0, width: "100px" }}
                  type="number"
                  min="0"
                  step="0.05"
                  placeholder="Multiplier"
                  value={p.multiplier}
                  onChange={(e) => {
                    const next = draft.slice();
                    next[idx] = {
                      ...next[idx],
                      multiplier: e.target.value,
                    };
                    setDraft(next);
                  }}
                />
                <span style={{ fontSize: "11px", color: "#666" }}>
                  id: {p.id || slugId(p.name || "")}
                </span>
                <button
                  style={{
                    ...styles.button,
                    margin: 0,
                    background: "#fee",
                    color: "#a00",
                  }}
                  onClick={() =>
                    setDraft(draft.filter((_, i) => i !== idx))
                  }
                >
                  Remove
                </button>
              </div>
            ))}
            <button
              style={{ ...styles.button, marginTop: "4px" }}
              onClick={() =>
                setDraft([
                  ...draft,
                  { id: "", name: "", multiplier: 1 },
                ])
              }
            >
              + Add Pay Type
            </button>
            {configErr && (
              <div
                style={{ color: "red", fontSize: "12px", marginTop: "6px" }}
              >
                {configErr}
              </div>
            )}
            <div style={{ display: "flex", gap: "8px", marginTop: "8px" }}>
              <button
                style={{ ...styles.primaryButton, margin: 0 }}
                onClick={handleSaveConfig}
                disabled={savingConfig}
              >
                {savingConfig ? "Saving..." : "Save Pay Types"}
              </button>
              <button
                style={{ ...styles.button, margin: 0 }}
                onClick={() => setShowConfig(false)}
                disabled={savingConfig}
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>

      {/* View toggle: Detail (per-entry blocks) vs Overview (grouped). */}
      <div
        style={{
          display: "flex",
          gap: "0",
          marginBottom: "8px",
          border: "1px solid #d0d4d9",
          borderRadius: "6px",
          overflow: "hidden",
          width: "fit-content",
        }}
      >
        {[
          { id: "detail", label: "Detail" },
          { id: "overview", label: "Overview" },
        ].map((v) => (
          <button
            key={v.id}
            style={{
              padding: "8px 18px",
              border: "none",
              background: view === v.id ? "#1a5fb4" : "white",
              color: view === v.id ? "white" : "#222",
              fontWeight: view === v.id ? 700 : 500,
              cursor: "pointer",
              fontSize: "13px",
              fontFamily: "inherit",
            }}
            onClick={() => setView(v.id)}
          >
            {v.label}
          </button>
        ))}
      </div>

      <div
        style={{
          display: "flex",
          gap: "6px",
          flexWrap: "wrap",
          marginBottom: "12px",
        }}
      >
        {["pending", "approved", "locked", "all"].map((t) => (
          <button
            key={t}
            style={{
              ...styles.button,
              background: tab === t ? "#1a5fb4" : "#f3f4f6",
              color: tab === t ? "white" : "#222",
              fontWeight: tab === t ? 600 : 500,
              margin: 0,
            }}
            onClick={() => setTab(t)}
          >
            {t[0].toUpperCase() + t.slice(1)}
          </button>
        ))}
      </div>

      <div
        style={{
          display: "flex",
          gap: "8px",
          flexWrap: "wrap",
          alignItems: "flex-end",
          marginBottom: "12px",
        }}
      >
        <div>
          <label style={{ display: "block", fontSize: "12px", color: "#666" }}>
            Tech
          </label>
          <select
            style={{ ...styles.input, margin: 0, width: "200px" }}
            value={techFilter}
            onChange={(e) => setTechFilter(e.target.value)}
          >
            <option value="">All techs</option>
            {orgUsers.map((u) => (
              <option key={u.id} value={u.id}>
                {displayName(u.profile, u.email) || u.id.slice(0, 8)}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label style={{ display: "block", fontSize: "12px", color: "#666" }}>
            Customer
          </label>
          <select
            style={{ ...styles.input, margin: 0, width: "200px" }}
            value={customerFilter}
            onChange={(e) => {
              setCustomerFilter(e.target.value);
              setLocationFilter("");
              setJobFilter("");
            }}
          >
            <option value="">All customers</option>
            {customers
              .slice()
              .sort((a, b) => (a.name || "").localeCompare(b.name || ""))
              .map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name || c.id.slice(0, 8)}
                </option>
              ))}
          </select>
        </div>
        <div>
          <label style={{ display: "block", fontSize: "12px", color: "#666" }}>
            Location
          </label>
          <select
            style={{ ...styles.input, margin: 0, width: "200px" }}
            value={locationFilter}
            onChange={(e) => {
              setLocationFilter(e.target.value);
              setJobFilter("");
            }}
            disabled={!customerFilter}
          >
            <option value="">All locations</option>
            {filterLocations
              .slice()
              .sort((a, b) => (a.name || "").localeCompare(b.name || ""))
              .map((l) => (
                <option key={l.id} value={l.id}>
                  {l.name || l.id.slice(0, 8)}
                </option>
              ))}
          </select>
        </div>
        <div>
          <label style={{ display: "block", fontSize: "12px", color: "#666" }}>
            Job
          </label>
          <select
            style={{ ...styles.input, margin: 0, width: "220px" }}
            value={jobFilter}
            onChange={(e) => setJobFilter(e.target.value)}
            disabled={!locationFilter}
          >
            <option value="">All jobs</option>
            {filterJobs.map((j) => (
              <option key={j.id} value={j.id}>
                {j.jobNumber ? `#${j.jobNumber} — ` : ""}
                {j.title || j.id.slice(0, 8)}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label style={{ display: "block", fontSize: "12px", color: "#666" }}>
            Range
          </label>
          <select
            style={{ ...styles.input, margin: 0, width: "190px" }}
            value={range}
            onChange={(e) => setRange(e.target.value)}
          >
            {payPeriodBounds && (
              <option value="current_period">
                Current pay period
              </option>
            )}
            <option value="week">Last 7 days</option>
            <option value="month">Last 30 days</option>
            <option value="quarter">Last 90 days</option>
            <option value="all">All time</option>
          </select>
        </div>
        {view === "overview" && (
          <div>
            <label style={{ display: "block", fontSize: "12px", color: "#666" }}>
              Group by
            </label>
            <select
              style={{ ...styles.input, margin: 0, width: "160px" }}
              value={groupByMode}
              onChange={(e) => setGroupByMode(e.target.value)}
            >
              <option value="tech">Tech</option>
              <option value="job">Job</option>
              <option value="customer">Customer</option>
              <option value="day">Day</option>
              <option value="week">Week</option>
              <option value="month">Month</option>
              <option value="year">Year</option>
            </select>
          </div>
        )}
        <button
          style={{ ...styles.button, margin: 0 }}
          onClick={handleExport}
          disabled={filtered.length === 0}
        >
          Export CSV
        </button>
        {view === "detail" && tab === "pending" && filtered.length > 0 && (
          <button
            style={{ ...styles.primaryButton, margin: 0 }}
            onClick={handleBulkApprove}
          >
            Approve All ({filtered.length})
          </button>
        )}
      </div>

      {range === "current_period" && payPeriodBounds && (
        <div
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: "8px",
            padding: "4px 10px",
            background: "#dde7f4",
            color: "#1a5fb4",
            border: "1px solid #1a5fb4",
            borderRadius: "12px",
            fontSize: "12px",
            fontWeight: 600,
            marginBottom: "8px",
          }}
          title={`From ${new Date(payPeriodBounds.startMs).toLocaleDateString()} to ${new Date(payPeriodBounds.endMs - 86400000).toLocaleDateString()}`}
        >
          📅 {payPeriodBounds.label}
        </div>
      )}

      <div
        style={{
          ...styles.card,
          maxWidth: "780px",
          fontSize: "13px",
          display: "flex",
          gap: "20px",
          flexWrap: "wrap",
        }}
      >
        <div>
          <strong>Entries:</strong> {filtered.length}
        </div>
        <div>
          <strong>Total Hours:</strong> {formatDuration(totalMinutes)} (
          {(totalMinutes / 60).toFixed(2)})
        </div>
        {totalDollars > 0 && (
          <div>
            <strong>Total Labor Cost:</strong> ${totalDollars.toFixed(2)}
          </div>
        )}
      </div>

      {filtered.length === 0 ? (
        <p style={{ color: "#666", fontStyle: "italic" }}>
          No entries match these filters.
        </p>
      ) : view === "overview" ? (
        <OverviewTable
          rows={overviewRows}
          groupByMode={groupByMode}
          sort={overviewSort}
          onSort={toggleSort}
          onRowClick={handleOverviewRowClick}
          totalMinutes={totalMinutes}
          totalDollars={totalDollars}
          formatDuration={formatDuration}
        />
      ) : (
        <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
          {filtered.map((e) => {
            const badge = STATUS_BADGE[e.status || "pending"];
            const u = userById.get(e.userId);
            const techName = u
              ? displayName(u.profile, u.email)
              : e.userName || e.userEmail || "Unknown";
            const rate = parseFloat(u?.hourlyRate) || 0;
            const mult = payTypeMultiplier(e.payType);
            const decHours = (e.durationMinutes || 0) / 60;
            const cost = rate * mult * decHours;
            return (
              <li key={e.id} style={{ ...styles.card, maxWidth: "780px" }}>
                <div
                  style={{
                    display: "flex",
                    gap: "8px",
                    alignItems: "center",
                    flexWrap: "wrap",
                  }}
                >
                  <strong>{techName}</strong>
                  <span style={{ fontSize: "13px" }}>
                    {formatDate(e.startAt)} ·{" "}
                    {formatDuration(e.durationMinutes)}
                    {rate > 0 && ` · $${cost.toFixed(2)}`}
                  </span>
                  <span
                    style={{
                      fontSize: "11px",
                      padding: "2px 8px",
                      background: badge.bg,
                      color: badge.color,
                      borderRadius: "10px",
                      fontWeight: 600,
                    }}
                  >
                    {badge.label}
                  </span>
                  <span
                    style={{
                      fontSize: "11px",
                      padding: "2px 8px",
                      background: "#eef",
                      color: "#33c",
                      borderRadius: "10px",
                    }}
                  >
                    {payTypeLabel(e.payType)} ({mult}x)
                  </span>
                  <span
                    style={{
                      fontSize: "11px",
                      color: "#666",
                      marginLeft: "auto",
                    }}
                  >
                    {e.source === "clock" ? "Clock" : "Manual"}
                  </span>
                </div>
                <div style={{ fontSize: "13px", marginTop: "4px" }}>
                  {jobLabel(e.jobId)}
                </div>
                {e.notes && (
                  <div
                    style={{
                      fontSize: "13px",
                      color: "#444",
                      fontStyle: "italic",
                      marginTop: "4px",
                    }}
                  >
                    "{e.notes}"
                  </div>
                )}
                <div
                  style={{ fontSize: "12px", color: "#666", marginTop: "4px" }}
                >
                  {formatDateTime(e.startAt)} → {formatDateTime(e.endAt)}
                  {e.approvedAt && (
                    <>
                      {" · approved by "}
                      {e.approvedByName || e.approvedBy} on{" "}
                      {formatDateTime(e.approvedAt)}
                    </>
                  )}
                </div>
                {e.status !== "locked" && (
                  <div
                    style={{
                      display: "flex",
                      gap: "6px",
                      alignItems: "center",
                      marginTop: "8px",
                      flexWrap: "wrap",
                    }}
                  >
                    <label style={{ fontSize: "12px", color: "#666" }}>
                      Override pay type:
                    </label>
                    <select
                      style={{ ...styles.input, margin: 0, width: "200px" }}
                      value={e.payType || "straight"}
                      onChange={(ev) =>
                        handleSetEntryPayType(e.id, ev.target.value)
                      }
                    >
                      {effectivePayTypes.map((pt) => (
                        <option key={pt.id} value={pt.id}>
                          {pt.name} ({pt.multiplier}x)
                        </option>
                      ))}
                    </select>
                  </div>
                )}
                <div
                  style={{
                    display: "flex",
                    gap: "6px",
                    marginTop: "8px",
                    flexWrap: "wrap",
                  }}
                >
                  {(e.status || "pending") === "pending" && (
                    <button
                      style={{ ...styles.primaryButton, margin: 0 }}
                      onClick={() => actions.approveTimeEntry(e.id)}
                    >
                      Approve
                    </button>
                  )}
                  {e.status === "approved" && (
                    <>
                      <button
                        style={{ ...styles.button, margin: 0 }}
                        onClick={() => actions.lockTimeEntry(e.id)}
                      >
                        Lock
                      </button>
                      <button
                        style={{ ...styles.button, margin: 0 }}
                        onClick={() => actions.unapproveTimeEntry(e.id)}
                      >
                        Unapprove
                      </button>
                    </>
                  )}
                  {e.status === "locked" && (
                    <button
                      style={{ ...styles.button, margin: 0 }}
                      onClick={() => actions.unlockTimeEntry(e.id)}
                    >
                      Unlock
                    </button>
                  )}
                  <button
                    style={{
                      ...styles.button,
                      margin: 0,
                      background: "#fee",
                      color: "#a00",
                    }}
                    onClick={() => {
                      if (window.confirm("Delete this time entry?")) {
                        actions.deleteTimeEntry(e.id);
                      }
                    }}
                  >
                    Delete
                  </button>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </>
  );
}

// ------- Overview table -------
// Compact rollup table: one row per bucket. Click a row to drill into
// Detail filtered by that bucket (tech / job / customer only; date
// buckets just switch view). Header click toggles sort direction on
// that column.
const GROUP_COL_LABEL = {
  tech: "Tech",
  job: "Job",
  customer: "Customer",
  day: "Day",
  week: "Week",
  month: "Month",
  year: "Year",
};

function OverviewTable({
  rows,
  groupByMode,
  sort,
  onSort,
  onRowClick,
  totalMinutes,
  totalDollars,
  formatDuration,
}) {
  const labelCol = ["day", "week", "month", "year"].includes(groupByMode)
    ? "key"
    : "label";

  const SortHeader = ({ id, label, align = "left" }) => {
    const active = sort.col === id;
    return (
      <th
        onClick={() => onSort(id)}
        style={{
          padding: "8px 10px",
          textAlign: align,
          fontSize: "12px",
          color: active ? "#1a5fb4" : "#5a6470",
          cursor: "pointer",
          borderBottom: "2px solid #e6e8eb",
          background: "#f6f8fb",
          fontWeight: 700,
          userSelect: "none",
          whiteSpace: "nowrap",
        }}
      >
        {label}
        {active ? (sort.dir === "asc" ? " ▲" : " ▼") : ""}
      </th>
    );
  };

  return (
    <div
      style={{
        ...styles.card,
        padding: 0,
        overflow: "hidden",
        maxWidth: "780px",
      }}
    >
      <table
        style={{
          width: "100%",
          borderCollapse: "collapse",
          fontSize: "13px",
        }}
      >
        <thead>
          <tr>
            <SortHeader id={labelCol} label={GROUP_COL_LABEL[groupByMode]} />
            <SortHeader id="minutes" label="Hours" align="right" />
            <SortHeader id="dollars" label="Labor Cost" align="right" />
            <SortHeader id="entries" label="Entries" align="right" />
          </tr>
        </thead>
        <tbody>
          {rows.map((b) => (
            <tr
              key={b.key}
              onClick={() => onRowClick(b)}
              style={{
                cursor: "pointer",
                borderBottom: "1px solid #f0f0f0",
              }}
              onMouseEnter={(e) =>
                (e.currentTarget.style.background = "#eef2f6")
              }
              onMouseLeave={(e) =>
                (e.currentTarget.style.background = "transparent")
              }
            >
              <td style={{ padding: "8px 10px", color: "#1a5fb4" }}>
                {b.label}
              </td>
              <td
                style={{
                  padding: "8px 10px",
                  textAlign: "right",
                  fontVariantNumeric: "tabular-nums",
                }}
              >
                {formatDuration(b.minutes)}{" "}
                <span style={{ color: "#888", fontSize: "11px" }}>
                  ({(b.minutes / 60).toFixed(2)})
                </span>
              </td>
              <td
                style={{
                  padding: "8px 10px",
                  textAlign: "right",
                  fontVariantNumeric: "tabular-nums",
                  color: b.dollars > 0 ? "#222" : "#aaa",
                }}
              >
                {b.dollars > 0 ? `$${b.dollars.toFixed(2)}` : "—"}
              </td>
              <td
                style={{
                  padding: "8px 10px",
                  textAlign: "right",
                  fontVariantNumeric: "tabular-nums",
                  color: "#5a6470",
                }}
              >
                {b.entries}
              </td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr style={{ background: "#f6f8fb" }}>
            <td
              style={{
                padding: "8px 10px",
                fontWeight: 700,
                borderTop: "2px solid #e6e8eb",
              }}
            >
              Total ({rows.length} {rows.length === 1 ? "group" : "groups"})
            </td>
            <td
              style={{
                padding: "8px 10px",
                textAlign: "right",
                fontWeight: 700,
                borderTop: "2px solid #e6e8eb",
                fontVariantNumeric: "tabular-nums",
              }}
            >
              {formatDuration(totalMinutes)}{" "}
              <span style={{ color: "#888", fontSize: "11px" }}>
                ({(totalMinutes / 60).toFixed(2)})
              </span>
            </td>
            <td
              style={{
                padding: "8px 10px",
                textAlign: "right",
                fontWeight: 700,
                borderTop: "2px solid #e6e8eb",
                fontVariantNumeric: "tabular-nums",
              }}
            >
              {totalDollars > 0 ? `$${totalDollars.toFixed(2)}` : "—"}
            </td>
            <td style={{ borderTop: "2px solid #e6e8eb" }} />
          </tr>
        </tfoot>
      </table>
      <div
        style={{
          padding: "8px 12px",
          fontSize: "11px",
          color: "#888",
          background: "#fafbfc",
          borderTop: "1px solid #f0f0f0",
        }}
      >
        Click a row to see the entries that make up that group.
      </div>
    </div>
  );
}
